import { graphql } from "@acto/gql";
import { PaginatedDataAccessResult, PagingOptions } from "@acto/taskpane/core/data-access/types";
import { useMemo } from "react";
import { GetNotesQuery, NoteType } from "@acto/gql/graphql";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { Note } from "@acto/taskpane/core/domain";

const NotesQuery = graphql(`
  query GetNotes($first: Int!, $after: String, $filter: NoteFilter) {
    notes(first: $first, after: $after, filter: $filter) {
      __typename
      edges {
        __typename
        cursor
        node {
          id
          __typename
          clientId
          meetingId
          content
          title
          createdAt
          updatedAt
          summary
          openTodosCount
        }
      }
      pageInfo {
        __typename
        hasNextPage
        endCursor
      }
    }
  }
`);

interface NoteFilterOptions {
  clientId?: string;
  meetingId?: string;
  type?: NoteType;
  dateRange?: { startDate: string; endDate: string };
}

export function useNotes({
  clientId,
  meetingId,
  type,
  dateRange,
  limit = 10,
}: PagingOptions & NoteFilterOptions): PaginatedDataAccessResult<Note> {
  const startDate = dateRange?.startDate;
  const endDate = dateRange?.endDate;
  const filter = useMemo(
    () => ({
      clientId,
      meetingId,
      type,
      dateRange: startDate && endDate ? { startDate, endDate } : undefined,
    }),
    [clientId, meetingId, type, startDate, endDate],
  );

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter, refetch } = usePagination({
    query: NotesQuery,
    limit,
    connection: (data: GetNotesQuery) => data.notes,
    variables: { filter },
  });

  const notes = useMemo(() => data?.map(Note.fromGraphql), [data]);

  return {
    data: notes,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
    refetch,
  };
}
